"use client";

import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * EPIC-046 part 2 phase F — outbound URL preview card.
 *
 * Stocktwits / Twitter pattern: when a post body contains an http(s)
 * URL, render the FIRST one as a thumbnail + title + description card
 * below the body. The fetch is server-cached + SSRF-guarded; we only
 * show the card after a successful resolve. URL not resolvable → no
 * card (silently drop).
 */
export function LinkPreviewCard({ url }: { url: string }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | {
        kind: "ok";
        title: string | null;
        description: string | null;
        imageUrl: string | null;
        siteName: string | null;
        host: string;
      }
    | { kind: "hidden" }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void api
      .fetchOgPreview(url)
      .then((res) => {
        if (cancelled) return;
        if (!res.preview) {
          setState({ kind: "hidden" });
          return;
        }
        const host = safeHost(res.preview.finalUrl ?? url);
        setState({
          kind: "ok",
          title: res.preview.title,
          description: res.preview.description,
          imageUrl: res.preview.imageUrl,
          siteName: res.preview.siteName,
          host,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "hidden" });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (state.kind === "hidden") return null;
  if (state.kind === "loading") {
    return (
      <div
        className="mt-3 flex h-[88px] items-center gap-3 overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[12px] text-[var(--color-text3)]"
        aria-hidden
      >
        Loading preview…
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener nofollow ugc"
      className="mt-3 flex overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] transition-colors hover:border-[var(--color-text3)]"
    >
      {state.imageUrl && (
        <img
          src={state.imageUrl}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          className="h-[110px] w-[110px] flex-none object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 p-3">
        <span className="text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
          {state.siteName ?? state.host}
        </span>
        <span className="line-clamp-2 text-[13px] font-bold text-[var(--color-text)]">
          {state.title ?? state.host}
        </span>
        {state.description && (
          <span className="line-clamp-2 text-[12px] text-[var(--color-text2)]">
            {state.description}
          </span>
        )}
      </div>
    </a>
  );
}

function safeHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
